/**
 * `.rl-breakdown` — the reel in five figures, and the panel that says what is
 * behind them (CAE-1552).
 *
 * The figures are `reelStats`, which the Contest Overview's Reels section draws
 * the same five of (CAE-1574 put it in `contests/` for exactly this). The panel
 * is the kit's `Popover`, which already dismisses on Escape and on an outside
 * click and moves focus in and back out — CAE-1552's third acceptance criterion,
 * met by using the primitive the kit has rather than re-deriving it here.
 */
import { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import { resolveFixtureLabel } from '@s2w/core';
import type { ContestDetailReelOption, ContestFixture } from '../../../../api/contests';
import { reelOptionMarketLabel } from '../../../../contests/reel-option-market-label';
import { ChevronDownIcon, Popover } from '../../../../components/ui';
import type { ReelSlot } from './reel-slots';

/** `.rl-popover` is drawn at 360px, wider than the kit's 300px default. */
const POPOVER_WIDTH = 360;

const plural = (count: number, noun: string) => (count === 1 ? noun : `${noun}s`);

/** `.rl-breakdown-stat` — one figure and the word for it. */
const Stat = ({ value, label, warn }: { value: number; label: string; warn?: boolean }) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: '5px',
      fontSize: '13px',
      color: warn ? theme.tokens.color.warn : theme.tokens.color.ink3,
    })}
  >
    <Box
      component="span"
      sx={(theme: Theme) => ({
        fontFamily: theme.tokens.font.mono,
        fontSize: '15px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: warn ? theme.tokens.color.warn : theme.tokens.color.ink,
      })}
    >
      {value}
    </Box>
    <Box component="span">{label}</Box>
  </Box>
);

interface MarketGroup {
  marketLabel: string;
  count: number;
}

interface FixtureGroup {
  contestFixtureId: string;
  fixtureLabel: string;
  markets: MarketGroup[];
}

/**
 * The reel's options, grouped the way the operator built them: by fixture, then
 * by the market inside it.
 *
 * Keyed on `contestFixtureId` and, inside each, on `reelOptionMarketLabel`.
 *
 * **These rows are not the strip's `markets` figure.** That counts distinct
 * market labels across the whole reel; these count them *per fixture*, so three
 * fixtures each carrying "Match Result" are one market up on the strip and
 * three rows down here. Both are true of the same reel — the strip answers how
 * many kinds of bet it offers, the panel answers where each sits — but printed
 * one above the other they read as a contradiction, which is why the header
 * below no longer restates the figure.
 */
const groupByFixture = (
  options: readonly ContestDetailReelOption[],
  fixturesById: ReadonlyMap<string, ContestFixture>,
): FixtureGroup[] => {
  const byFixture = new Map<string, Map<string, number>>();

  for (const option of options) {
    const markets = byFixture.get(option.contestFixtureId) ?? new Map<string, number>();
    const label = reelOptionMarketLabel(option);
    markets.set(label, (markets.get(label) ?? 0) + 1);
    byFixture.set(option.contestFixtureId, markets);
  }

  return [...byFixture].map(([contestFixtureId, markets]) => {
    const fixture = fixturesById.get(contestFixtureId);
    return {
      contestFixtureId,
      fixtureLabel: fixture ? resolveFixtureLabel(fixture) : 'Fixture not found',
      markets: [...markets].map(([marketLabel, count]) => ({ marketLabel, count })),
    };
  });
};

export interface ReelBreakdownProps {
  slot: ReelSlot;
  fixturesById: ReadonlyMap<string, ContestFixture>;
}

export const ReelBreakdown = ({ slot, fixturesById }: ReelBreakdownProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const options = slot.reel?.options ?? [];

  const groups = useMemo(() => groupByFixture(options, fixturesById), [options, fixturesById]);

  /*
   * Nothing at all on an empty reel — not the prototype's italic "No outcomes
   * yet — add some to configure this reel."
   *
   * `reels-app.jsx` renders that line *and* the `.rl-empty` block directly
   * beneath it, which on the real screen reads as the same fact twice over
   * three sentences, under two identical "+ Add outcomes" buttons. The block is
   * the better half: it names the reel, says what "configured" actually means
   * ("at least two enabled options"), and carries the CTA. Same call CAE-1550
   * made in dropping its "Clear filters" link — a second control for an action
   * already offered beside it. Seen side by side on the live screen.
   */
  if (slot.stats.options === 0) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
      <Stat value={slot.stats.options} label={plural(slot.stats.options, 'outcome')} />
      <Stat value={slot.stats.enabled} label="enabled" />
      <Stat value={slot.stats.fixtures} label={plural(slot.stats.fixtures, 'fixture')} />
      <Stat value={slot.stats.markets} label={plural(slot.stats.markets, 'market')} />
      {/* Only when there are any: zero suspended is not a figure worth a slot. */}
      {slot.stats.suspended === 0 ? null : (
        <Stat value={slot.stats.suspended} label="suspended" warn />
      )}

      {/*
       * Last in the strip, one 18px gap past the final figure — and **no
       * `marginLeft: 'auto'`**, though `.rl-breakdown-btn` carries one.
       *
       * That rule never fires in the handoff. There `.rl-breakdown` is a
       * shrink-to-fit flex *item* of `.rl-detail-meta`, so the strip is only
       * as wide as its contents and the auto margin has no free space to
       * absorb: measured on the prototype, the pill ends 391px short of the
       * card's right edge, hard against the figures. The reference screenshot
       * and the screen README ("a View breakdown pill at the end") agree.
       *
       * Ported here the rule *does* fire — this strip is a block child of the
       * head's column, so it runs the full width — and the pill lands out at
       * an edge the design never puts it near. Dropped rather than kept
       * inert, so the next reader is not left re-deriving why it does nothing
       * (CAE-1608).
       */}
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        sx={(theme: Theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '12.5px',
          fontWeight: 500,
          padding: '5px 10px',
          borderRadius: `${theme.tokens.radius.idChip}px`,
          color: theme.tokens.color.accent,
          border: `${theme.tokens.border.width} solid transparent`,
          transition: 'background 0.12s, border-color 0.12s',
          ...(open
            ? {
                backgroundColor: theme.tokens.color.accentSoft,
                borderColor: theme.tokens.componentColor.tierBadgeBorder,
              }
            : {
                '&:hover': {
                  backgroundColor: theme.tokens.color.accentSoft,
                  borderColor: theme.tokens.componentColor.tierBadgeBorder,
                },
              }),
        })}
      >
        View breakdown
        <Box
          aria-hidden
          component="span"
          sx={{
            display: 'inline-grid',
            placeItems: 'center',
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        >
          <ChevronDownIcon />
        </Box>
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        label={`Breakdown of ${slot.title}`}
        onClose={() => setOpen(false)}
        width={POPOVER_WIDTH}
      >
        <Box
          sx={(theme: Theme) => ({
            padding: '14px 16px 10px',
            borderBottom: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surfaceMuted,
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              fontSize: '10.5px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: theme.tokens.color.ink4,
            })}
          >
            Breakdown
          </Box>
          <Box
            sx={(theme: Theme) => ({
              marginTop: '4px',
              fontSize: '13.5px',
              fontWeight: 600,
              lineHeight: 1.4,
              letterSpacing: '-0.005em',
              color: theme.tokens.color.ink,
            })}
          >
            {slot.stats.options} {plural(slot.stats.options, 'outcome')} across{' '}
            {slot.stats.fixtures} {plural(slot.stats.fixtures, 'fixture')}
          </Box>
        </Box>

        <Box
          sx={{
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            maxHeight: '50vh',
            overflowY: 'auto',
          }}
        >
          {groups.map((group) => (
            <Box
              key={group.contestFixtureId}
              sx={(theme: Theme) => ({
                border: theme.tokens.border.default,
                borderRadius: `${theme.tokens.radius.control}px`,
                overflow: 'hidden',
              })}
            >
              <Box
                sx={(theme: Theme) => ({
                  padding: '9px 12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: theme.tokens.color.ink,
                  backgroundColor: theme.tokens.color.surfaceMuted,
                  borderBottom: theme.tokens.border.default,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {group.fixtureLabel}
              </Box>
              {group.markets.map((market) => (
                <Box
                  key={market.marketLabel}
                  sx={(theme: Theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    padding: '7px 12px',
                    fontSize: '12.5px',
                    borderBottom: theme.tokens.border.default,
                    '&:last-of-type': { borderBottom: 0 },
                  })}
                >
                  <Box
                    component="span"
                    sx={(theme: Theme) => ({
                      color: theme.tokens.color.ink2,
                      fontWeight: 500,
                    })}
                  >
                    {market.marketLabel}
                  </Box>
                  <Box
                    component="span"
                    sx={(theme: Theme) => ({
                      color: theme.tokens.color.ink3,
                      fontFamily: theme.tokens.font.mono,
                      fontVariantNumeric: 'tabular-nums',
                    })}
                  >
                    {market.count} {plural(market.count, 'outcome')}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
